import { useState } from 'react';
import { InformeRequest, FormularioInforme } from '../types/informe';
import { informeService } from '../services/informeService';
import { useAuth } from '../contexts/AuthContext';

interface UseCrearInformeReturn {
  /** Función para crear un informe */
  crearInforme: (data: FormularioInforme & { partidoId: number }) => Promise<void>;
  /** Si está en proceso de creación */
  isLoading: boolean;
  /** Error si ocurre */
  error: string | null;
}

/**
 * Hook para manejar la creación de informes
 */
export const useCrearInforme = (): UseCrearInformeReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  /**
   * Convierte los datos del formulario a formato de request
   */
  const convertirFormularioARequest = (data: FormularioInforme & { partidoId: number }): InformeRequest => {
    return {
      nombreCompleto: data.nombreCompleto,
      nombreEquipo: data.nombreEquipo,
      letraEquipo: data.letraEquipo as any,
      lateralidad: data.lateralidad as any,
      nivel: data.nivel as any,
      posicion: data.posicion as any,
      año: Number(data.año),
      dorsal: data.dorsal ? Number(data.dorsal) : undefined,
      categoriaFutura: data.categoriaFutura || undefined,
      partidoId: data.partidoId,
      seccion: data.seccion,
      // Habilidades
      potenciaFisica: data.potenciaFisica,
      velocidad: data.velocidad,
      tomaDecision: data.tomaDecision,
      chute: data.chute,
      tecnicaIndividual: data.tecnicaIndividual,
      observaciones: data.observaciones || undefined
    };
  };

  /**
   * Crea un nuevo informe
   */
  const crearInforme = async (data: FormularioInforme & { partidoId: number }): Promise<void> => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Convertir datos del formulario
      const informeData = convertirFormularioARequest(data);
      
      // Crear FormData para enviar con la foto
      const formData = new FormData();
      
      // Añadir cada campo individualmente al FormData
      Object.entries(informeData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      
      // Añadir foto si existe
      if (data.foto) {
        formData.append('foto', data.foto);
      }
      
      // Enviar al servicio
      await informeService.crearInforme(formData);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    crearInforme,
    isLoading,
    error
  };
};